import {
  adminListsQuerySchema,
  adminProductsQuerySchema,
  adminStatsQuerySchema,
  type AdminListsQuery,
  type AdminProductsQuery,
  type AdminStatsQuery,
} from "@listou/shared/schemas/admin";
import {
  attachEmailSchema,
  requestMagicLinkSchema,
  updateProfileSchema,
  verifyAttachEmailSchema,
  verifyAuthCodeSchema,
  type AttachEmailInput,
  type RequestMagicLinkInput,
  type UpdateProfileInput,
  type VerifyAttachEmailInput,
  type VerifyAuthCodeInput,
} from "@listou/shared/schemas/auth";
import { createCheckoutSchema, type CreateCheckoutInput } from "@listou/shared/schemas/billing";
import {
  addPlaceSchema,
  addSelfSchema,
  attachListSchema,
  claimPlaceSchema,
  createGroupSchema,
  colorRulesSchema,
  drawSchema,
  duplicateGroupSchema,
  exclusionsSchema,
  openPlaceSchema,
  placeCodeSchema,
  unsubscribeSchema,
  updateGroupSchema,
  updatePlaceSchema,
  type AddPlaceInput,
  type AddSelfInput,
  type ClaimPlaceInput,
  type CreateGroupInput,
  type ColorRulesInput,
  type DrawInput,
  type DuplicateGroupInput,
  type ExclusionsInput,
  type OpenPlaceInput,
  type PlaceCodeInput,
  type UpdateGroupInput,
  type UpdatePlaceInput,
} from "@listou/shared/schemas/groups";
import {
  assistantRequestSchema,
  createListSchema,
  createWishSchema,
  inviteListMemberSchema,
  catalogueCategoriesQuerySchema,
  catalogueQuerySchema,
  productPreviewSchema,
  reviewSchema,
  reserveWishSchema,
  updateListSchema,
  updateWishSchema,
  type AssistantRequestInput,
  type CreateListInput,
  type CreateWishInput,
  type CatalogueCategoriesQuery,
  type CatalogueQuery,
  type InviteListMemberInput,
  type ReviewInput,
  type ReserveWishInput,
  type UpdateListInput,
  type UpdateWishInput,
} from "@listou/shared/schemas/lists";
import type {
  DepartmentPage,
  DepartmentsOverview,
  NamePageResponse,
  NameSummary,
  TrendsPage,
  YearDepartmentPage,
  YearPage,
  YearsOverview,
} from "@listou/shared/schemas/prenoms";
import { createUploadSchema } from "@listou/shared/schemas/uploads";
import type {
  AcceptListInvitationResponse,
  AdminListsResponse,
  AdminStatsResponse,
  AdminAffiliateResponse,
  AdminDesignsResponse,
  AdminSignInsResponse,
  AdminProductsResponse,
  AdminReprocessResponse,
  AdminSystemResponse,
  AdminUsageResponse,
  // Aliased: ApiError below is the thrown class, this is the wire shape.
  ApiError as ApiErrorBody,
  AssistantResponse,
  AuthCodeResponse,
  AuthConfig,
  CatalogueCategoriesResponse,
  CatalogueResponse,
  ReviewResponse,
  ReviewsResponse,
  BillingRedirectResponse,
  BillingStateResponse,
  CsrfResponse,
  DrawResponse,
  GroupResponse,
  GroupsResponse,
  IdentitiesResponse,
  ListGiftsResponse,
  ListInvitationResponse,
  ListMembersResponse,
  ListResponse,
  ListsResponse,
  OkResponse,
  PendingUploadResponse,
  ProductPreviewResponse,
  ProductSearchResponse,
  PublicGroupResponse,
  PublicListResponse,
  PublicProductResponse,
  RecommendationsResponse,
  ReservationResponse,
  SessionsResponse,
  TrendsResponse,
  UploadResponse,
  Viewer,
  WishesResponse,
  WishResponse,
} from "@listou/shared/types/api";

/**
 * Thin fetch wrapper. It is not a token store: the session lives in an httpOnly cookie the
 * browser attaches on its own, and no JavaScript here can read it. That is deliberate —
 * a token held in JS is readable by any script on the page, including a compromised
 * dependency, while an httpOnly cookie is not.
 */

const CSRF_COOKIE = "listou_csrf";

/** The CSRF cookie is the one cookie that IS readable, so it can be echoed in a header. */
const readCsrfToken = (): string | null => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${CSRF_COOKIE}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields?: Record<string, string[]>,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const BASE = "/api/v1";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

type Transport = (path: string, init: RequestInit) => Promise<Response>;

let transport: Transport = (path, init) => fetch(path, init);

/**
 * Where requests go. The browser fetches over the network; the server, rendering a page,
 * swaps in its own in-process call (see apps/server/src/share/ssr.ts), so the same loaders
 * and query functions run in both places. Called once at startup, never per request.
 */
export const setApiTransport = (next: Transport) => {
  transport = next;
};

export const api = async <T>(
  path: string,
  options: RequestInit & { json?: unknown } = {},
): Promise<T> => {
  const method = options.method ?? (options.json ? "POST" : "GET");
  const headers = new Headers(options.headers);

  if (options.json !== undefined) headers.set("content-type", "application/json");

  if (!SAFE_METHODS.has(method)) {
    const csrf = readCsrfToken();
    if (csrf) headers.set("x-csrf-token", csrf);
  }

  const response = await transport(`${BASE}${path}`, {
    ...options,
    method,
    headers,
    // Same-origin in every deployment (the API process also serves the SPA), so the cookie
    // rides along without any cross-site cookie configuration.
    credentials: "same-origin",
    body: options.json === undefined ? options.body : JSON.stringify(options.json),
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      body?.error.code ?? "unknown_error",
      body?.error.message ?? "Something went wrong.",
      body?.error.details?.fields,
      body?.error.requestId,
    );
  }

  return payload as T;
};

// --- Typed endpoints ---------------------------------------------------------

export const authApi = {
  config: () => api<AuthConfig>("/auth/config"),
  me: () => api<Viewer>("/auth/me"),
  /** Recovers the CSRF cookie when a browser has dropped it but the session is still live. */
  refreshCsrf: () => api<CsrfResponse>("/auth/csrf"),
  requestMagicLink: (input: RequestMagicLinkInput) =>
    // Validate before sending: the user sees the error inline instead of as a 400, and it
    // is the same schema the server enforces, so the two can never disagree.
    api<OkResponse>("/auth/magic-link", { json: requestMagicLinkSchema.parse(input) }),
  /**
   * The typed-code half of the same grant. The code is normalised by the shared schema
   * before it goes out, so spacing and case are the input's problem, not the server's.
   */
  verifyAuthCode: (input: VerifyAuthCodeInput) =>
    api<AuthCodeResponse>("/auth/magic-link/verify", { json: verifyAuthCodeSchema.parse(input) }),
  logout: () => api<void>("/auth/logout", { method: "POST" }),
  /** A guest adding an address: a code is mailed to it... */
  attachEmail: (input: AttachEmailInput) =>
    api<OkResponse>("/auth/email", { json: attachEmailSchema.parse(input) }),
  /** ...and typed back. `merged`: the address had an account, which this browser now is. */
  verifyEmail: (input: VerifyAttachEmailInput) =>
    api<{ ok: true; merged: boolean }>("/auth/email/verify", {
      json: verifyAttachEmailSchema.parse(input),
    }),
  /** The name that signs the caller's lists on public pages. */
  updateProfile: (input: UpdateProfileInput) =>
    api<OkResponse>("/auth/me", { method: "PATCH", json: updateProfileSchema.parse(input) }),
  deleteAccount: () => api<void>("/auth/me", { method: "DELETE" }),
  identities: () => api<IdentitiesResponse>("/auth/identities"),
  sessions: () => api<SessionsResponse>("/auth/sessions"),
  revokeSession: (id: string) => api<void>(`/auth/sessions/${id}`, { method: "DELETE" }),
  // A full navigation, not fetch: the OAuth flow needs the browser to follow redirects to
  // Google and back.
  googleUrl: (redirectTo?: string) =>
    `${BASE}/auth/google${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ""}`,
};

const enc = encodeURIComponent;

export const listsApi = {
  list: () => api<ListsResponse>("/lists"),
  get: (id: string) => api<ListResponse>(`/lists/${enc(id)}`),
  create: (input: CreateListInput) =>
    api<ListResponse>("/lists", { json: createListSchema.parse(input) }),
  update: (id: string, input: UpdateListInput) =>
    api<ListResponse>(`/lists/${enc(id)}`, {
      method: "PATCH",
      json: updateListSchema.parse(input),
    }),
  remove: (id: string) => api<void>(`/lists/${enc(id)}`, { method: "DELETE" }),
  recommendations: (id: string) =>
    api<RecommendationsResponse>(`/lists/${enc(id)}/recommendations`),
  rotateSlug: (id: string) =>
    api<ListResponse>(`/lists/${enc(id)}/rotate-slug`, { method: "POST" }),
  preview: (id: string) => api<PublicListResponse>(`/lists/${enc(id)}/preview`),
  gifts: (id: string) => api<ListGiftsResponse>(`/lists/${enc(id)}/gifts`),
};

export const wishesApi = {
  list: (listId: string) => api<WishesResponse>(`/lists/${enc(listId)}/wishes`),
  create: (listId: string, input: CreateWishInput) =>
    api<WishResponse>(`/lists/${enc(listId)}/wishes`, { json: createWishSchema.parse(input) }),
  update: (listId: string, wishId: string, input: UpdateWishInput) =>
    api<WishResponse>(`/lists/${enc(listId)}/wishes/${enc(wishId)}`, {
      method: "PATCH",
      json: updateWishSchema.parse(input),
    }),
  remove: (listId: string, wishId: string) =>
    api<void>(`/lists/${enc(listId)}/wishes/${enc(wishId)}`, { method: "DELETE" }),
  reorder: (listId: string, ids: string[]) =>
    api<WishesResponse>(`/lists/${enc(listId)}/wishes/reorder`, { json: { ids } }),
};

export const membersApi = {
  list: (listId: string) => api<ListMembersResponse>(`/lists/${enc(listId)}/members`),
  invite: (listId: string, input: InviteListMemberInput) =>
    api<ListInvitationResponse>(`/lists/${enc(listId)}/invitations`, {
      json: inviteListMemberSchema.parse(input),
    }),
  revokeInvitation: (listId: string, invitationId: string) =>
    api<void>(`/lists/${enc(listId)}/invitations/${enc(invitationId)}`, { method: "DELETE" }),
  remove: (listId: string, userId: string) =>
    api<void>(`/lists/${enc(listId)}/members/${enc(userId)}`, { method: "DELETE" }),
  accept: (token: string) =>
    api<AcceptListInvitationResponse>("/list-invitations/accept", { json: { token } }),
};

export const assistantApi = {
  ask: (listId: string, input: AssistantRequestInput) =>
    api<AssistantResponse>(`/lists/${enc(listId)}/assistant`, {
      json: assistantRequestSchema.parse(input),
    }),
};

export const productsApi = {
  preview: (url: string) =>
    api<ProductPreviewResponse>("/products/preview", { json: productPreviewSchema.parse({ url }) }),
  search: (q: string, signal?: AbortSignal) =>
    api<ProductSearchResponse>(`/products/search?q=${enc(q)}`, { signal }),
};

export const reviewsApi = {
  /** One review per person and product: this creates it or replaces it. */
  save: (productId: string, input: ReviewInput) =>
    api<ReviewResponse>(`/products/${enc(productId)}/reviews/mine`, {
      method: "PUT",
      json: reviewSchema.parse(input),
    }),
  remove: (productId: string) =>
    api<void>(`/products/${enc(productId)}/reviews/mine`, { method: "DELETE" }),
};

/**
 * The bytes go straight to the bucket, never through the API: sign, PUT, complete. The
 * PUT is the one request here that leaves our origin, so it bypasses `api()` — it must not
 * carry the CSRF header or the cookie, and the bucket answers in XML, not our error shape.
 */
const putToBucket = async (url: string, file: File, contentType: string) => {
  const response = await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "content-type": contentType },
    credentials: "omit",
  });
  if (!response.ok) {
    throw new ApiError(response.status, "upload_failed", "L'image n'a pas pu être envoyée.");
  }
};

export const uploadsApi = {
  image: async (file: File) => {
    const input = createUploadSchema.parse({
      filename: file.name || "image",
      contentType: file.type,
      size: file.size,
    });
    const { upload } = await api<PendingUploadResponse>("/uploads", { json: input });
    await putToBucket(upload.putUrl, file, input.contentType);
    return api<UploadResponse>(`/uploads/${enc(upload.id)}/complete`, { method: "POST" });
  },
};

export const publicApi = {
  list: (slug: string) => api<PublicListResponse>(`/public/lists/${enc(slug)}`),
  reserve: (slug: string, input: ReserveWishInput) =>
    api<ReservationResponse>(`/public/lists/${enc(slug)}/reservations`, {
      json: reserveWishSchema.parse(input),
    }),
  cancelReservation: (reservationId: string, token: string) =>
    api<void>(`/public/reservations/${enc(reservationId)}`, {
      method: "DELETE",
      json: { token },
    }),
  /**
   * Revalidated every time: the anonymous response may be cached by the browser, and after
   * signing in or reviewing, the page must see the personal fields (myReview) at once.
   */
  product: (slug: string) =>
    api<PublicProductResponse>(`/public/products/${enc(slug)}`, { cache: "no-cache" }),
  reviews: (slug: string, cursor: string) =>
    api<ReviewsResponse>(`/public/products/${enc(slug)}/reviews?cursor=${enc(cursor)}`),
  /** Anyone may report a review, account or not: enough reports hide it. */
  reportReview: (id: string) => api<void>(`/public/reviews/${enc(id)}/report`, { method: "POST" }),
  catalogue: (query: CatalogueQuery) => {
    const parsed = catalogueQuerySchema.parse(query);
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(parsed)) if (value) params.set(key, String(value));
    return api<CatalogueResponse>(`/public/catalogue?${params}`);
  },
  catalogueCategories: (query: CatalogueCategoriesQuery) => {
    const { occasion } = catalogueCategoriesQuerySchema.parse(query);
    return api<CatalogueCategoriesResponse>(
      `/public/catalogue/categories${occasion ? `?occasion=${enc(occasion)}` : ""}`,
    );
  },
  trends: () => api<TrendsResponse>("/public/trends"),
  /** Plain URLs for <img> and downloads: images are served by the API, not fetched here. */
  /** `version` busts the cache, so a preview follows the list's latest appearance. */
  shareImageUrl: (slug: string, format: "og" | "story" | "square", version?: string) =>
    `${BASE}/public/lists/${enc(slug)}/share.png?format=${format}${version ? `&v=${enc(version)}` : ""}`,
  qrUrl: (slug: string) => `${BASE}/public/lists/${enc(slug)}/qr.svg`,
};

/** Groups, from the organizer's side. Every call answers with the whole group. */
export const groupsApi = {
  list: () => api<GroupsResponse>("/groups"),
  get: (id: string) => api<GroupResponse>(`/groups/${enc(id)}`),
  create: (input: CreateGroupInput) =>
    api<GroupResponse>("/groups", { json: createGroupSchema.parse(input) }),
  update: (id: string, input: UpdateGroupInput) =>
    api<GroupResponse>(`/groups/${enc(id)}`, {
      method: "PATCH",
      json: updateGroupSchema.parse(input),
    }),
  remove: (id: string) => api<void>(`/groups/${enc(id)}`, { method: "DELETE" }),
  rotateSlug: (id: string) =>
    api<GroupResponse>(`/groups/${enc(id)}/rotate-slug`, { method: "POST" }),
  addPlace: (id: string, input: AddPlaceInput) =>
    api<GroupResponse>(`/groups/${enc(id)}/places`, { json: addPlaceSchema.parse(input) }),
  updatePlace: (id: string, placeId: string, input: UpdatePlaceInput) =>
    api<GroupResponse>(`/groups/${enc(id)}/places/${enc(placeId)}`, {
      method: "PATCH",
      json: updatePlaceSchema.parse(input),
    }),
  removePlace: (id: string, placeId: string, confirmRevealed?: number) =>
    api<GroupResponse>(
      `/groups/${enc(id)}/places/${enc(placeId)}${
        confirmRevealed === undefined ? "" : `?confirmRevealed=${confirmRevealed}`
      }`,
      { method: "DELETE" },
    ),
  releasePlace: (id: string, placeId: string) =>
    api<GroupResponse>(`/groups/${enc(id)}/places/${enc(placeId)}/release`, { method: "POST" }),
  rotatePlaceLink: (id: string, placeId: string) =>
    api<GroupResponse>(`/groups/${enc(id)}/places/${enc(placeId)}/rotate-link`, {
      method: "POST",
    }),
  resendPlaceMail: (id: string, placeId: string) =>
    api<OkResponse>(`/groups/${enc(id)}/places/${enc(placeId)}/resend`, { method: "POST" }),
  setColorRules: (id: string, input: ColorRulesInput) =>
    api<GroupResponse>(`/groups/${enc(id)}/color-rules`, {
      method: "PUT",
      json: colorRulesSchema.parse(input),
    }),
  setExclusions: (id: string, input: ExclusionsInput) =>
    api<GroupResponse>(`/groups/${enc(id)}/exclusions`, {
      method: "PUT",
      json: exclusionsSchema.parse(input),
    }),
  draw: (id: string, input: DrawInput) =>
    api<DrawResponse>(`/groups/${enc(id)}/draw`, { json: drawSchema.parse(input) }),
  duplicate: (id: string, input: DuplicateGroupInput) =>
    api<GroupResponse>(`/groups/${enc(id)}/duplicate`, {
      json: duplicateGroupSchema.parse(input),
    }),
};

/** Groups, from the visitors' side: by slug, with or without an account. */
export const publicGroupsApi = {
  get: (slug: string) => api<PublicGroupResponse>(`/public/groups/${enc(slug)}`),
  claim: (slug: string, placeId: string, input: ClaimPlaceInput = {}) =>
    api<PublicGroupResponse>(`/public/groups/${enc(slug)}/places/${enc(placeId)}/claim`, {
      json: claimPlaceSchema.parse(input),
    }),
  addSelf: (slug: string, input: AddSelfInput) =>
    api<PublicGroupResponse>(`/public/groups/${enc(slug)}/places`, {
      json: addSelfSchema.parse(input),
    }),
  open: (slug: string, input: OpenPlaceInput) =>
    api<PublicGroupResponse>(`/public/groups/${enc(slug)}/open`, {
      json: openPlaceSchema.parse(input),
    }),
  unbind: (slug: string) =>
    api<PublicGroupResponse>(`/public/groups/${enc(slug)}/me/unbind`, { method: "POST" }),
  attachList: (slug: string, listId: string) =>
    api<PublicGroupResponse>(`/public/groups/${enc(slug)}/me/list`, {
      json: attachListSchema.parse({ listId }),
    }),
  reveal: (slug: string) =>
    api<PublicGroupResponse>(`/public/groups/${enc(slug)}/me/reveal`, { method: "POST" }),
  sendCode: (slug: string, placeId: string) =>
    api<OkResponse>(`/public/groups/${enc(slug)}/places/${enc(placeId)}/code`, {
      method: "POST",
    }),
  verifyCode: (slug: string, placeId: string, input: PlaceCodeInput) =>
    api<PublicGroupResponse>(`/public/groups/${enc(slug)}/places/${enc(placeId)}/code/verify`, {
      json: placeCodeSchema.parse(input),
    }),
  mailLink: (slug: string, placeId: string) =>
    api<OkResponse>(`/public/groups/${enc(slug)}/places/${enc(placeId)}/mail-link`, {
      method: "POST",
    }),
  unsubscribe: (token: string) =>
    api<OkResponse>("/public/unsubscribe", { json: unsubscribeSchema.parse({ token }) }),
};

export const billingApi = {
  get: () => api<BillingStateResponse>("/billing"),
  /**
   * Both return a Stripe-hosted URL for the caller to assign to `window.location.href`.
   * They are not followed here: fetch would follow a redirect itself and hand the JSON
   * parser Stripe's HTML.
   */
  checkout: (input: CreateCheckoutInput) =>
    api<BillingRedirectResponse>("/billing/checkout", { json: createCheckoutSchema.parse(input) }),
  // No payload: the portal opens for the caller's own workspace and returns to a path the
  // server chooses.
  portal: () => api<BillingRedirectResponse>("/billing/portal", { method: "POST" }),
};

export const adminApi = {
  stats: (query: AdminStatsQuery) => {
    const { days } = adminStatsQuerySchema.parse(query);
    return api<AdminStatsResponse>(`/admin/stats?days=${days}`);
  },
  lists: (query: AdminListsQuery) => {
    const { sort } = adminListsQuerySchema.parse(query);
    return api<AdminListsResponse>(`/admin/lists?sort=${sort}`);
  },
  usage: (query: AdminStatsQuery) => {
    const { days } = adminStatsQuerySchema.parse(query);
    return api<AdminUsageResponse>(`/admin/usage?days=${days}`);
  },
  system: () => api<AdminSystemResponse>("/admin/system"),
  affiliate: () => api<AdminAffiliateResponse>("/admin/affiliate"),
  products: (query: AdminProductsQuery) => {
    const { host, sort, filter } = adminProductsQuerySchema.parse(query);
    const params = new URLSearchParams({ sort, filter, ...(host ? { host } : {}) });
    return api<AdminProductsResponse>(`/admin/products?${params}`);
  },
  reprocess: (productId: string) =>
    api<AdminReprocessResponse>(`/admin/products/${encodeURIComponent(productId)}/reprocess`, {
      method: "POST",
    }),
  designs: (query: AdminStatsQuery) => {
    const { days } = adminStatsQuerySchema.parse(query);
    return api<AdminDesignsResponse>(`/admin/designs?days=${days}`);
  },
  signIns: (query: AdminStatsQuery) => {
    const { days } = adminStatsQuerySchema.parse(query);
    return api<AdminSignInsResponse>(`/admin/sign-ins?days=${days}`);
  },
};

export const namesApi = {
  index: () => api<NameSummary[]>("/public/prenoms"),
  get: (slug: string) => api<NamePageResponse>(`/public/prenoms/${enc(slug)}`),
  trends: () => api<TrendsPage>("/public/prenoms/tendances"),
  years: () => api<YearsOverview>("/public/prenoms/annees"),
  year: (year: string) => api<YearPage>(`/public/prenoms/annees/${enc(year)}`),
  yearDepartment: (year: string, department: string) =>
    api<YearDepartmentPage>(`/public/prenoms/annees/${enc(year)}/departements/${enc(department)}`),
  departments: () => api<DepartmentsOverview>("/public/prenoms/departements"),
  department: (department: string) =>
    api<DepartmentPage>(`/public/prenoms/departements/${enc(department)}`),
};

type CardFormat = "og" | "story" | "square";
/** Plain URLs for <img>, downloads and `og:image`: the cards are drawn by the API. */
export const nameCards = {
  name: (slug: string, format: CardFormat, year?: number) =>
    `${BASE}/public/prenoms/${enc(slug)}/share.png?format=${format}${year ? `&annee=${year}` : ""}`,
  year: (year: number, format: CardFormat) =>
    `${BASE}/public/prenoms/annees/${year}/share.png?format=${format}`,
  department: (department: string, format: CardFormat) =>
    `${BASE}/public/prenoms/departements/${enc(department)}/share.png?format=${format}`,
  yearDepartment: (year: number, department: string, format: CardFormat) =>
    `${BASE}/public/prenoms/annees/${year}/departements/${enc(department)}/share.png?format=${format}`,
  page: (page: "index" | "years" | "departments" | "trends", format: CardFormat) =>
    `${BASE}/public/prenoms/cards/${page}?format=${format}`,
};
